import React, { useEffect, useState } from "react";
import CnLoginView from "./cn-login-view";
import {
  checkCnRelay,
  loginWithPhone,
  saveCnRelay,
  sendPhoneCode,
  startWechatLogin,
} from "./cn-auth-service";
import {
  captureCnLoginIntent,
  clearCnLoginIntent,
  readCnLoginIntent,
  saveCnLoginIntent,
} from "./cn-login-intent";

const normalizePhone = (value) => value.replace(/[\s-]/g, "");

export default function CnUserLogin() {
  const [intent, setIntent] = useState(null);
  const [phase, setPhase] = useState("validating");
  const [clientDisplayName, setClientDisplayName] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    const captured = captureCnLoginIntent();
    setIntent(captured);
    if (!captured) {
      setError("登录请求无效或已过期");
      setPhase("invalid");
      return;
    }
    if (captured.flow === "pairing") {
      setPhase("pairing");
      return;
    }
    checkCnRelay(captured)
      .then((result) => {
        setClientDisplayName(result.clientDisplayName || "请求登录的应用");
        setPhase("credentials");
      })
      .catch(() => {
        clearCnLoginIntent();
        setError("登录请求无效或已过期");
        setPhase("invalid");
      });
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setTimeout(
      () => setCooldown((value) => value - 1),
      1000
    );
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const verifyPairing = async () => {
    if (!/^\d{6}$/.test(pairingCode)) {
      setError("请输入 6 位配对码");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const next = { ...intent, pairingCode };
      await checkCnRelay(next);
      saveCnLoginIntent(next);
      setIntent(readCnLoginIntent());
      setPhase("credentials");
    } catch (_) {
      setError("配对码无效或已过期");
    } finally {
      setBusy(false);
    }
  };

  const deliver = async (tokens) => {
    const delivery = await saveCnRelay({ ...intent, tokens });
    clearCnLoginIntent();
    if (intent.flow === "callback") {
      if (!delivery.redirectUri) throw new Error("登录回调地址缺失");
      window.location.replace(delivery.redirectUri);
    } else {
      window.location.replace("/auth-granted");
    }
  };

  const requestCode = async () => {
    const phone = normalizePhone(phoneNumber);
    if (!/^(\+86)?\d{11}$/.test(phone)) {
      setError("请输入有效的中国大陆手机号");
      return;
    }
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await sendPhoneCode(phone);
      setInfo("验证码已发送");
      setCooldown(60);
    } catch (_) {
      setError("验证码发送失败，请稍后再试");
    } finally {
      setBusy(false);
    }
  };

  const phoneLogin = async () => {
    const phone = normalizePhone(phoneNumber);
    if (!/^(\+86)?\d{11}$/.test(phone) || !/^\d{6}$/.test(verifyCode)) {
      setError("请输入手机号和 6 位验证码");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await deliver(await loginWithPhone(phone, verifyCode));
    } catch (_) {
      setError("登录失败，请检查验证码后重试");
    } finally {
      setBusy(false);
    }
  };

  const wechatLogin = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await startWechatLogin();
      if (!result?.loginUrl || !result?.state)
        throw new Error("Invalid WeChat login response");
      saveCnLoginIntent({ ...intent, wechatState: result.state });
      window.location.assign(result.loginUrl);
    } catch (_) {
      setError("微信登录暂时不可用，请稍后再试");
      setBusy(false);
    }
  };

  return (
    <CnLoginView
      {...{
        phase,
        clientDisplayName,
        pairingCode,
        setPairingCode,
        phoneNumber,
        setPhoneNumber,
        verifyCode,
        setVerifyCode,
        cooldown,
        busy,
        error,
        info,
      }}
      onVerifyPairing={verifyPairing}
      onSendCode={requestCode}
      onPhoneLogin={phoneLogin}
      onWechatLogin={wechatLogin}
    />
  );
}
